/**
 * AfterClick 专用 XHR 响应拦截器
 *
 * 核心机制：
 * 1. 临时包装 window.XMLHttpRequest（不影响已创建的实例）
 * 2. 对每个新 XHR 实例，缓冲所有「请求完成」相关的业务回调
 * 3. 当请求 URL 匹配 targetApiUrl 时，拦截响应数据，等待上报回调完成
 * 4. 上报完成（或超时）后，恢复调用业务端的原始回调
 * 5. 拦截完成后自动恢复原 XMLHttpRequest
 */

/** 捕获到的 XHR 响应数据 */
export interface CapturedXHRResponse {
  /** HTTP 响应状态码 */
  status: number
  /** HTTP 响应状态文本 */
  statusText: string
  /** 响应体文本 */
  responseText: string
  /** 原始响应（可能为对象，取决于 responseType） */
  response: unknown
  /** 响应头 */
  responseHeaders: Record<string, string>
}

/** AfterClick 拦截配置 */
export interface AfterClickInterceptConfig {
  /** 目标接口 URL（包含匹配，大小写敏感） */
  targetApiUrl: string
  /** 等待上报完成的最大超时时间（ms），默认 5000 */
  timeout?: number
  /** 捕获到响应后的异步回调，完成后放行业务端回调 */
  onCapture: (response: CapturedXHRResponse) => Promise<void>
  /** 需要排除的 URL（SDK 上报地址），避免误拦截 */
  excludeUrl?: string
}

/**
 * 判断请求 URL 是否匹配目标（排除指定 URL）
 */
function urlMatchesTarget(url: string, target: string, excludeUrl?: string): boolean {
  if (!url || !target) return false
  // 排除 SDK 上报地址
  if (excludeUrl && url.includes(excludeUrl)) return false
  return url.includes(target)
}

/**
 * 截断字符串
 */
function truncateString(str: string, maxLen: number): string {
  if (!str || str.length <= maxLen) return str
  return str.slice(0, maxLen) + `...[TRUNCATED:${str.length}]`
}

/**
 * 激活一次 AfterClick XHR 拦截
 *
 * 设计要点：
 * - 包装当前 window.XMLHttpRequest（可能已被 NetworkModule 代理）
 * - 缓冲业务端设置的 load/error/abort/timeout/readystatechange/loadend 回调
 * - send() 调用时根据 isTarget 决定是直连还是拦截
 * - 拦截成功后调用 onCapture，完成后恢复业务回调
 * - 超时或非目标请求均安全放行
 *
 * @param config 拦截配置
 * @returns 取消拦截的函数
 */
export function activateAfterClickIntercept(config: AfterClickInterceptConfig): () => void {
  const { targetApiUrl, timeout = 5000, onCapture, excludeUrl } = config

  if (typeof window === 'undefined' || !window.XMLHttpRequest) {
    return () => {}
  }

  let active = true
  const PreviousXHR = window.XMLHttpRequest

  // 如果已有拦截器，直接返回清理函数（防止重复拦截）
  if ((PreviousXHR as unknown as { _xt_afterclick_intercepted?: boolean })._xt_afterclick_intercepted) {
    return () => { active = false }
  }

  // 标记已拦截
  ;(PreviousXHR as unknown as { _xt_afterclick_intercepted?: boolean })._xt_afterclick_intercepted = true

  /** 恢复原始 XMLHttpRequest */
  const cleanup = () => {
    if (active) {
      active = false
      ;(PreviousXHR as unknown as { _xt_afterclick_intercepted?: boolean })._xt_afterclick_intercepted = false
      window.XMLHttpRequest = PreviousXHR
    }
  }

  // 超时保护：若长时间未匹配到目标请求，自动清理
  const autoCleanupTimer = setTimeout(cleanup, timeout + 500)

  /** 拦截版本的 XMLHttpRequest 构造函数 */
  function InterceptedXHR(this: Record<string, unknown>): void {
    const innerXHR = new PreviousXHR()
    let requestUrl = ''
    let isTarget = false
    const proxyThis = this

    // ── 代理只读属性 ──
    const READ_ONLY_PROPS = [
      'readyState', 'response', 'responseText', 'responseXML',
      'status', 'statusText', 'upload',
    ]
    READ_ONLY_PROPS.forEach(prop => {
      Object.defineProperty(proxyThis, prop, {
        get() { return (innerXHR as unknown as Record<string, unknown>)[prop] },
        enumerable: true,
        configurable: true,
      })
    })

    // ── 代理可读写属性 ──
    const READWRITE_PROPS = ['responseType', 'timeout', 'withCredentials']
    READWRITE_PROPS.forEach(prop => {
      Object.defineProperty(proxyThis, prop, {
        get() { return (innerXHR as unknown as Record<string, unknown>)[prop] },
        set(val: unknown) { (innerXHR as unknown as Record<string, unknown>)[prop] = val },
        enumerable: true,
        configurable: true,
      })
    })

    // ── 直接代理的进度事件（不需要拦截） ──
    ;['onloadstart', 'onprogress'].forEach(prop => {
      Object.defineProperty(proxyThis, prop, {
        get() { return (innerXHR as unknown as Record<string, unknown>)[prop] },
        set(val: unknown) { (innerXHR as unknown as Record<string, unknown>)[prop] = val },
        enumerable: true,
        configurable: true,
      })
    })

    // ── 缓冲「请求完成」相关回调 ──
    // 这些回调在 send() 之前就可能被设置，需要先缓存，等 send() 确认 isTarget 后再决定如何处理
    let _onload: ((this: XMLHttpRequest, ev: ProgressEvent) => unknown) | null = null
    let _onerror: ((this: XMLHttpRequest, ev: ProgressEvent) => unknown) | null = null
    let _onabort: ((this: XMLHttpRequest, ev: ProgressEvent) => unknown) | null = null
    let _ontimeout: ((this: XMLHttpRequest, ev: ProgressEvent) => unknown) | null = null
    let _onreadystatechange: ((this: XMLHttpRequest, ev: Event) => unknown) | null = null
    let _onloadend: ((this: XMLHttpRequest, ev: ProgressEvent) => unknown) | null = null

    // 通过 addEventListener 注册的监听器
    const _loadListeners: Array<{ listener: EventListenerOrEventListenerObject; options?: boolean | AddEventListenerOptions }> = []
    const _loadendListeners: Array<{ listener: EventListenerOrEventListenerObject; options?: boolean | AddEventListenerOptions }> = []
    const _errorListeners: Array<{ listener: EventListenerOrEventListenerObject; options?: boolean | AddEventListenerOptions }> = []
    const _abortListeners: Array<{ listener: EventListenerOrEventListenerObject; options?: boolean | AddEventListenerOptions }> = []
    const _timeoutListeners: Array<{ listener: EventListenerOrEventListenerObject; options?: boolean | AddEventListenerOptions }> = []

    // 定义属性设置器（缓冲，不直接设到 innerXHR）
    const defineBufferedProp = (
      name: string,
      getter: () => unknown,
      setter: (val: unknown) => void
    ) => {
      Object.defineProperty(proxyThis, name, {
        get: getter,
        set: setter,
        enumerable: true,
        configurable: true,
      })
    }

    defineBufferedProp('onload',
      () => _onload,
      (v) => { _onload = v as typeof _onload }
    )
    defineBufferedProp('onerror',
      () => _onerror,
      (v) => { _onerror = v as typeof _onerror }
    )
    defineBufferedProp('onabort',
      () => _onabort,
      (v) => { _onabort = v as typeof _onabort }
    )
    defineBufferedProp('ontimeout',
      () => _ontimeout,
      (v) => { _ontimeout = v as typeof _ontimeout }
    )
    defineBufferedProp('onreadystatechange',
      () => _onreadystatechange,
      (v) => { _onreadystatechange = v as typeof _onreadystatechange }
    )
    defineBufferedProp('onloadend',
      () => _onloadend,
      (v) => { _onloadend = v as typeof _onloadend }
    )

    // ── 工具函数：安全调用监听器 ──
    const callListener = (listener: EventListenerOrEventListenerObject, event: Event) => {
      try {
        if (typeof listener === 'function') {
          listener.call(proxyThis, event)
        } else if (listener && typeof listener.handleEvent === 'function') {
          listener.handleEvent(event)
        }
      } catch (e) {
        // 业务回调异常不影响 SDK
      }
    }

    /** 触发业务端所有「成功完成」回调 */
    const dispatchSuccessCallbacks = () => {
      const loadEvent = new ProgressEvent('load')
      const loadendEvent = new ProgressEvent('loadend')

      // readystatechange(4)
      if (_onreadystatechange) {
        try { _onreadystatechange.call(innerXHR, new Event('readystatechange')) } catch {}
      }

      // onload
      if (_onload) {
        try { _onload.call(innerXHR, loadEvent) } catch {}
      }

      // addEventListener('load', ...) 监听器
      _loadListeners.forEach(({ listener }) => callListener(listener, loadEvent))

      // onloadend
      if (_onloadend) {
        try { _onloadend.call(innerXHR, loadendEvent) } catch {}
      }

      // addEventListener('loadend', ...) 监听器
      _loadendListeners.forEach(({ listener }) => callListener(listener, loadendEvent))
    }

    /** 触发业务端失败回调 */
    const dispatchErrorCallbacks = (type: 'error' | 'abort' | 'timeout') => {
      const event = new ProgressEvent(type)
      const loadendEvent = new ProgressEvent('loadend')

      if (type === 'error' && _onerror) {
        try { _onerror.call(innerXHR, event) } catch {}
      }
      if (type === 'abort' && _onabort) {
        try { _onabort.call(innerXHR, event) } catch {}
      }
      if (type === 'timeout' && _ontimeout) {
        try { _ontimeout.call(innerXHR, event) } catch {}
      }

      const listenerMap = { error: _errorListeners, abort: _abortListeners, timeout: _timeoutListeners }
      listenerMap[type].forEach(({ listener }) => callListener(listener, event))

      // loadend 总会触发
      if (_onloadend) {
        try { _onloadend.call(innerXHR, loadendEvent) } catch {}
      }
      _loadendListeners.forEach(({ listener }) => callListener(listener, loadendEvent))
    }

    // ── open - 捕获 URL ──
    proxyThis['open'] = function(method: string, url: string | URL, ...args: unknown[]) {
      requestUrl = url instanceof URL ? url.href : String(url)
      isTarget = active && urlMatchesTarget(requestUrl, targetApiUrl, excludeUrl)
      ;(innerXHR.open as (...args: unknown[]) => void)(method, url, ...args)
    }

    // ── setRequestHeader ──
    proxyThis['setRequestHeader'] = function(name: string, value: string) {
      innerXHR.setRequestHeader(name, value)
    }

    // ── send - 核心拦截/转发逻辑 ──
    proxyThis['send'] = function(body?: Document | XMLHttpRequestBodyInit | null) {
      if (isTarget) {
        // 清除超时自动清理定时器（已匹配到目标，进入真正的拦截流程）
        clearTimeout(autoCleanupTimer)

        // 代理 readystatechange：状态 1-3 正常转发，状态 4 延迟到上报完成后触发
        if (_onreadystatechange) {
          innerXHR.onreadystatechange = function(ev: Event) {
            if (innerXHR.readyState < 4) {
              try { _onreadystatechange!.call(innerXHR, ev) } catch {}
            }
            // readyState === 4 由 load 事件处理后触发
          }
        }

        // 代理 error/abort/timeout（这些情况直接放行，不需要等上报）
        innerXHR.addEventListener('error', () => {
          dispatchErrorCallbacks('error')
          cleanup()
        })
        innerXHR.addEventListener('abort', () => {
          dispatchErrorCallbacks('abort')
          cleanup()
        })
        innerXHR.addEventListener('timeout', () => {
          dispatchErrorCallbacks('timeout')
          cleanup()
        })

        // 拦截 load 事件（核心）
        innerXHR.addEventListener('load', () => {
          // 1. 捕获响应数据
          const responseHeaders: Record<string, string> = {}
          try {
            const headerStr = innerXHR.getAllResponseHeaders()
            if (headerStr) {
              headerStr.split('\r\n').forEach(line => {
                const idx = line.indexOf(': ')
                if (idx > 0) {
                  responseHeaders[line.slice(0, idx).toLowerCase()] = line.slice(idx + 2)
                }
              })
            }
          } catch {}

          let responseText = ''
          try { responseText = innerXHR.responseText } catch {}

          const capturedResponse: CapturedXHRResponse = {
            status: innerXHR.status,
            statusText: innerXHR.statusText,
            responseText: truncateString(responseText, 10240),
            response: innerXHR.response,
            responseHeaders,
          }

          // 2. 调用上报回调 + 超时保护
          const reportPromise = onCapture(capturedResponse).catch(() => {})
          const timeoutPromise = new Promise<void>(resolve => setTimeout(resolve, timeout))

          Promise.race([reportPromise, timeoutPromise]).then(() => {
            // 3. 上报完成（或超时）后，放行业务端回调
            dispatchSuccessCallbacks()
            // 4. 清理拦截器
            cleanup()
          })
        })

      } else {
        // 非目标请求：将缓冲的回调直接连接到 innerXHR
        if (_onload) innerXHR.onload = _onload
        if (_onerror) innerXHR.onerror = _onerror
        if (_onabort) innerXHR.onabort = _onabort
        if (_ontimeout) innerXHR.ontimeout = _ontimeout
        if (_onreadystatechange) innerXHR.onreadystatechange = _onreadystatechange
        if (_onloadend) innerXHR.onloadend = _onloadend

        _loadListeners.forEach(({ listener, options }) => innerXHR.addEventListener('load', listener, options))
        _loadendListeners.forEach(({ listener, options }) => innerXHR.addEventListener('loadend', listener, options))
        _errorListeners.forEach(({ listener, options }) => innerXHR.addEventListener('error', listener, options))
        _abortListeners.forEach(({ listener, options }) => innerXHR.addEventListener('abort', listener, options))
        _timeoutListeners.forEach(({ listener, options }) => innerXHR.addEventListener('timeout', listener, options))
      }

      innerXHR.send(body)
    }

    // ── addEventListener - 缓冲「完成」类事件，透传其他事件 ──
    proxyThis['addEventListener'] = function(
      type: string,
      listener: EventListenerOrEventListenerObject,
      options?: boolean | AddEventListenerOptions
    ) {
      switch (type) {
        case 'load':
          _loadListeners.push({ listener, options })
          break
        case 'loadend':
          _loadendListeners.push({ listener, options })
          break
        case 'error':
          _errorListeners.push({ listener, options })
          break
        case 'abort':
          _abortListeners.push({ listener, options })
          break
        case 'timeout':
          _timeoutListeners.push({ listener, options })
          break
        default:
          innerXHR.addEventListener(type as keyof XMLHttpRequestEventMap, listener, options)
      }
    }

    proxyThis['removeEventListener'] = function(
      type: string,
      listener: EventListenerOrEventListenerObject,
      options?: boolean | EventListenerOptions
    ) {
      innerXHR.removeEventListener(type as keyof XMLHttpRequestEventMap, listener, options)
    }

    proxyThis['abort'] = function() {
      cleanup()
      innerXHR.abort()
    }

    proxyThis['getAllResponseHeaders'] = function() {
      return innerXHR.getAllResponseHeaders()
    }

    proxyThis['getResponseHeader'] = function(name: string) {
      return innerXHR.getResponseHeader(name)
    }

    proxyThis['overrideMimeType'] = function(mime: string) {
      innerXHR.overrideMimeType(mime)
    }

    proxyThis['dispatchEvent'] = function(event: Event) {
      return innerXHR.dispatchEvent(event)
    }
  }

  // 复制静态常量
  ;(InterceptedXHR as unknown as Record<string, number>).UNSENT = 0
  ;(InterceptedXHR as unknown as Record<string, number>).OPENED = 1
  ;(InterceptedXHR as unknown as Record<string, number>).HEADERS_RECEIVED = 2
  ;(InterceptedXHR as unknown as Record<string, number>).LOADING = 3
  ;(InterceptedXHR as unknown as Record<string, number>).DONE = 4

  // 设置原型链，保证 instanceof 检查
  InterceptedXHR.prototype = Object.create(PreviousXHR.prototype)
  InterceptedXHR.prototype.constructor = InterceptedXHR

  // 替换全局 XMLHttpRequest
  window.XMLHttpRequest = InterceptedXHR as unknown as typeof XMLHttpRequest

  return cleanup
}
